/**
 * Presentation helpers for `ScheduledPromotionDoc` — the web port of the computed properties on
 * iOS `ScheduledPromotion` (`ScheduledPromotion.swift:57-84`).
 *
 * Free functions, matching `event-utils.ts`, so they stay trivially unit-testable and nothing has
 * to wrap the raw Firestore document on the way out of `PromotionsService`.
 */

import { PromotionMediaItem, ScheduledPromotionDoc } from '../models/promotion.model';
import { isRenderableMedia, isVideoMedia, mediaStillUrl } from '../events/event-utils';

/** The promotion types that have a destination of their own. */
export const PROMOTION_TYPE_MEDIA = 'media';

/**
 * The gallery, in the order the admin authored it and with unshowable items dropped.
 *
 * Prefers the canonical `mediaItems` and falls back to the legacy `media` array, exactly as iOS's
 * `mediaValues` does — a promotion written before `mediaItems` existed still renders. The fallback
 * is not just a different key: `media` entries carry no `mediaThumbnailUrl`, so a video from there
 * has no poster and `app-event-media` stands its own first frame in instead.
 *
 * Empty is a real case every caller must handle: a promotion can carry no media at all (every
 * `type: "product"` and `type: "event"` document does), and the screen then renders text only.
 */
export function promotionMedia(promotion: ScheduledPromotionDoc | null | undefined): PromotionMediaItem[] {
  const canonical = (promotion?.mediaItems ?? []).filter(isRenderableMedia);
  if (canonical.length) return canonical;
  return (promotion?.media ?? []).filter(isRenderableMedia);
}

/**
 * Whether a promotion is the media kind. Trusts the document's own `type` — unlike iOS's
 * `resolvedType`, which also classifies by which id a promotion carries.
 *
 * That fallback exists on iOS for documents written before `type` was reliable, and there are
 * none: every one of the 94 documents across both projects carries an explicit type. Inferring
 * here would only add a way to guess wrong.
 */
export function isMediaPromotion(promotion: ScheduledPromotionDoc | null | undefined): boolean {
  return (promotion?.type ?? '').trim().toLowerCase() === PROMOTION_TYPE_MEDIA;
}

/**
 * Which way round an item actually is.
 *
 * `orientation` is the dashboard's word for it and wins when it says something we recognise, but it
 * is not trustworthy on its own: `before-order-promotion` documents carry no such field at all, and
 * the live landscape-tagged promotion is a 1254x1254 square. So the stored pixel dimensions are the
 * second opinion, and portrait is the floor — it is the shape that fills a phone, so guessing it
 * costs a crop at worst, where guessing landscape leaves bars around media that needed none.
 *
 * A caller holding the decoded element can do better still: `app-event-media` reports the real
 * shape through `aspectResolved` once the browser has the pixels.
 */
export function mediaOrientation(item: PromotionMediaItem | null | undefined): 'portrait' | 'landscape' {
  const declared = (item?.orientation ?? '').trim().toLowerCase();
  if (declared === 'landscape') return 'landscape';
  if (declared === 'portrait') return 'portrait';

  const width = item?.itemWidth ?? 0;
  const height = item?.itemHeight ?? 0;
  if (width > 0 && height > 0) return width > height ? 'landscape' : 'portrait';

  return 'portrait';
}

/**
 * How one item fills its frame. The rule is per ITEM, and it turns on shape alone:
 *
 *   portrait, image or video  → fill (cover)
 *   landscape, image or video → fit (contain), letterboxed over the blurred plate
 *
 * WEB DIVERGES FROM iOS HERE, deliberately. iOS letterboxes landscape images but fills landscape
 * VIDEO whatever its orientation (`MediaCardView.videoContentMode`), and this used to match. That
 * rule was written when the only clips were shot on phones — vertical, framed loose, nothing lost
 * to a crop. Produced 16:9 promotional video is now a supported content type, and cropping the
 * sides off a video someone cut to 16:9 is the same failure the landscape-image rule already exists
 * to prevent: the live landscape document is a square tagged "landscape", and filling the card ate
 * its top line of text. One shape rule for both media types is also simply easier to reason about
 * than a rule that asks what kind of file it is first.
 *
 * `fillLandscape` is the diner's own override, from the "Fill frame" control the promotion screen
 * offers on landscape VIDEO only. A landscape still has no such control and always letterboxes:
 * the control exists because motion can be worth a crop, and a still never is.
 *
 * Note the frame height does NOT change with orientation. Letterboxing over the blurred plate is
 * what absorbs the difference, which is why `mediaBackdropUrl` below is not optional dressing.
 */
export function mediaFitFor(
  item: PromotionMediaItem | null | undefined,
  options?: { fillLandscape?: boolean }
): 'cover' | 'contain' {
  if (mediaOrientation(item) !== 'landscape') return 'cover';
  return options?.fillLandscape && isVideoMedia(item) ? 'cover' : 'contain';
}

/**
 * The still used for the blurred plate behind a card — iOS `MediaCardView.background`, which blurs
 * the item's thumbnail (falling back to the media URL) at radius 16 and fills the card with it.
 *
 * It is what stops a letterboxed landscape image from sitting on flat bars, and it is why the fixed
 * card height above is not a problem. Null when the item has no still at all, and the card then
 * falls back to a plain surface fill.
 */
export function mediaBackdropUrl(item: PromotionMediaItem | null | undefined): string | null {
  return mediaStillUrl(item);
}
